import examples from "libphonenumber-js/mobile/examples";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isPossiblePhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js/max";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { isIndianUser } from "@/utils/getCountryCode";

export { isValidPhoneNumber, parsePhoneNumber };

const CALLING_CODE_ISO_PREFERENCE = {
  1: "US",
  7: "RU",
  44: "GB",
  91: "IN",
  971: "AE",
};

export function normalizeCallingCode(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function pickIsoFromCountryRow(country = {}) {
  const candidates = [country.iso2, country.isoCode, country.countryCode, country.code]
    .map((value) => String(value || "").trim().toUpperCase())
    .filter((value) => value.length === 2);

  return candidates[0] || null;
}

/** Resolve libphonenumber ISO (e.g. IN, AE) from selected dialling code. */
export function resolveCountryIsoForCallingCode(callingCode, countries = []) {
  const code = normalizeCallingCode(callingCode);
  if (!code) return null;

  const isos = getCountries().filter((iso) => getCountryCallingCode(iso) === code);
  if (!isos.length) return null;

  const apiMatches = (countries || []).filter(
    (country) => normalizeCallingCode(country.mobileCode) === code
  );

  for (const country of apiMatches) {
    const iso = pickIsoFromCountryRow(country);
    if (iso && isos.includes(iso)) return iso;
  }

  const preferred = CALLING_CODE_ISO_PREFERENCE[code];
  if (preferred && isos.includes(preferred)) return preferred;

  return isos[0];
}

export function getNationalMobileMaxDigits(countryIso) {
  if (!countryIso) return 15;

  try {
    const example = getExampleNumber(countryIso, examples);
    const length = example?.nationalNumber?.length;
    if (Number.isFinite(length) && length > 0) return length;
  } catch {
    // ignore
  }

  return 15;
}

export function clampNationalDigitsToCountry(national, countryIso) {
  let digits = String(national ?? "").replace(/\D/g, "");
  if (!digits) return digits;

  try {
    const example = countryIso ? getExampleNumber(countryIso, examples) : null;
    const exampleMax = example?.nationalNumber?.length;
    if (Number.isFinite(exampleMax) && exampleMax > 0) {
      digits = digits.slice(0, exampleMax);
    }
  } catch {
    // ignore
  }

  if (countryIso) {
    while (
      digits.length > 0 &&
      validatePhoneNumberLength(digits, countryIso) === "TOO_LONG"
    ) {
      digits = digits.slice(0, -1);
    }
  }

  return digits;
}

export function getNationalDigitsFromPhoneValue(value, fallbackCountry) {
  const raw = String(value ?? "");
  if (!raw) {
    return { national: "", callingCode: "", country: fallbackCountry || "" };
  }

  let parsed = null;
  try {
    parsed = parsePhoneNumber(raw);
  } catch {
    parsed = null;
  }

  const country = parsed?.country || fallbackCountry || "";
  let callingCode = String(parsed?.countryCallingCode || "").replace(/\D/g, "");
  if (!callingCode && country) {
    try {
      callingCode = String(getCountryCallingCode(country) || "").replace(/\D/g, "");
    } catch {
      callingCode = "";
    }
  }

  const allDigits = raw.replace(/\D/g, "");
  const national = parsed?.nationalNumber
    ? String(parsed.nationalNumber).replace(/\D/g, "")
    : callingCode && allDigits.startsWith(callingCode)
      ? allDigits.slice(callingCode.length)
      : allDigits;

  return { national, callingCode, country };
}

export function getNationalMobileMaxDigitsForCallingCode(callingCode, countries = []) {
  const iso = resolveCountryIsoForCallingCode(callingCode, countries);
  return getNationalMobileMaxDigits(iso);
}

export function sanitizeNationalMobileNumber(rawValue, callingCode, countries = []) {
  const digits = String(rawValue ?? "").replace(/\D/g, "");
  const iso = resolveCountryIsoForCallingCode(callingCode, countries);
  return clampNationalDigitsToCountry(digits, iso);
}

export function validateBookingMobileNumber(mobileCode, mobileNumber, countries = []) {
  const code = normalizeCallingCode(mobileCode);
  const digits = String(mobileNumber ?? "").replace(/\D/g, "");

  if (!code) return "Select a country code";
  if (!digits) return "Mobile number is required";

  const iso = resolveCountryIsoForCallingCode(code, countries);
  if (!iso) {
    return /^[0-9]{7,15}$/.test(digits)
      ? ""
      : "Enter a valid mobile number";
  }

  const expectedDigits = getNationalMobileMaxDigits(iso);
  const lengthResult = validatePhoneNumberLength(digits, iso);

  if (lengthResult === "TOO_LONG") {
    return `Enter up to ${expectedDigits} digits for +${code}`;
  }

  if (lengthResult === "TOO_SHORT" || lengthResult === "INVALID_LENGTH") {
    return `Enter a valid ${expectedDigits}-digit mobile number for +${code}`;
  }

  const e164 = `+${code}${digits}`;

  // Booking forms: enforce dialling code + national length (not strict carrier prefixes).
  if (isPossiblePhoneNumber(e164)) {
    return "";
  }

  if (!lengthResult && digits.length === expectedDigits) {
    return "";
  }

  return `Enter a valid mobile number for +${code}`;
}

export function getDefaultPhoneCountry() {
  return isIndianUser() ? "IN" : "AE";
}

export function validatePhoneValue(value) {
  if (!value) return "Mobile number is required";
  if (!isValidPhoneNumber(value)) return "Enter a valid mobile number";
  return "";
}

export function parsePhoneForApi(value) {
  if (!value) return null;

  try {
    const parsed = parsePhoneNumber(value);
    if (!parsed) return null;

    return {
      countryCode: `+${parsed.countryCallingCode}`,
      phoneNo: String(parsed.nationalNumber || ""),
    };
  } catch {
    return null;
  }
}

export function buildPhoneInputValueFromParts(countryCode, mobileNumber) {
  const code = normalizeCallingCode(countryCode);
  const digits = String(mobileNumber ?? "").replace(/\D/g, "");
  if (!code || !digits) return undefined;
  return `+${code}${digits}`;
}

export function limitPhoneValueToCountryMax(value, fallbackCountry) {
  if (value == null || value === "") return value;

  const { national, callingCode, country } = getNationalDigitsFromPhoneValue(
    value,
    fallbackCountry
  );
  const resolvedCountry = fallbackCountry || country;
  const limitedNational = clampNationalDigitsToCountry(national, resolvedCountry);

  if (!limitedNational || limitedNational === national) {
    if (limitedNational === national) return value;
    return limitedNational;
  }

  if (!callingCode) return limitedNational;
  return `+${callingCode}${limitedNational}`;
}

export function isCallingCodeOnlyValue(value, countryIso) {
  const raw = String(value ?? "").trim();
  if (!raw) return true;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return true;

  if (countryIso) {
    try {
      const code = String(getCountryCallingCode(countryIso) || "").replace(/\D/g, "");
      if (code && digits === code) return true;
    } catch {
      // ignore
    }
  }

  return /^\+\d{1,4}$/.test(raw.replace(/\s/g, ""));
}

export function toE164FromLocalDigits(digits, country = getDefaultPhoneCountry()) {
  const national = String(digits ?? "").replace(/\D/g, "");
  if (!national) return "";

  let callingCode = "";
  try {
    callingCode = String(getCountryCallingCode(country) || "").replace(/\D/g, "");
  } catch {
    callingCode = "";
  }

  if (callingCode && national.startsWith(callingCode) && national.length > callingCode.length) {
    return limitPhoneValueToCountryMax(`+${national}`, country);
  }

  return limitPhoneValueToCountryMax(`+${callingCode}${national}`, country);
}
